import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  ClipboardList,
  GitBranch,
  CalendarCheck,
  Stethoscope,
  MapPin,
  AlertTriangle,
  Award,
  Activity,
  Clock,
  Loader2,
  ArrowRight,
  ArrowUpRight,
  TrendingUp,
  TrendingDown,
  Minus,
  Hourglass,

  CheckCircle2,
  Gauge as GaugeIcon,
} from "lucide-react";
import { API } from "@/config/api";
import { OnlineNow } from "@/components/OnlineNow";
import DrilldownModal from "@/components/dashboard/DrilldownModal";
import {
  C,
  SS_STAGE_COLOR,
  TT_STAGE_COLOR,
  Sparkline,
  AreaTrend,
  RepBars,
  StatusDonut,
  Gauge,
  Ring,
  SegmentBar,
  RankBars,
  Funnel,
} from "@/components/dashboard/charts";

const REFRESH_MS = 5 * 60 * 1000;

const CONFIDENCE = {
  low: {
    tone: "amber",
    label: "Early forecast (low confidence)",
    tip: "Fewer than 5 working days of data. One busy or quiet day still swings the month-end projection heavily.",
  },
  medium: {
    tone: "amber",
    label: "Forecast developing",
    tip: "Between 5 and 14 working days of data. The projection is directional and firms up as the month goes on.",
  },
  high: {
    tone: "emerald",
    label: "Reliable forecast",
    tip: "15 or more working days of data, so the daily pace is stable enough to project the rest of the month.",
  },
};

const TONE = {
  emerald: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  amber: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  red: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
  slate: "bg-muted text-muted-foreground",
};

const num = (value, suffix = "") => (value === null || value === undefined ? "—" : `${value}${suffix}`);
const firstName = (name) => (name || "").split(" ")[0];

const relativeTime = (iso) => {
  if (!iso) return "";
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
};

const Section = ({ title, icon: Icon, note, children }) => (
  <section className="space-y-3" aria-label={title}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#2563eb]/10 text-[#2563eb]">
        <Icon className="w-4 h-4" />
      </span>
      <h2 className="text-base md:text-lg font-semibold tracking-tight">{title}</h2>
      {note && <span className="text-xs text-muted-foreground">{note}</span>}
    </div>
    {children}
  </section>
);

const Change = ({ value, suffix = "", percent = null }) => {
  if (value === null || value === undefined) return null;
  const Icon = value === 0 ? Minus : value > 0 ? TrendingUp : TrendingDown;
  const colour = value === 0 ? "text-muted-foreground" : value > 0 ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${colour}`}>
      <Icon className="w-3 h-3" />
      {value > 0 ? "+" : ""}
      {value}
      {suffix} from last month
      {percent !== null && ` (${percent > 0 ? "+" : ""}${percent}%)`}
    </span>
  );
};

/** Shell for every tile: one card, one headline number, optional visual underneath. */
const Tile = ({ label, value, sub, hint, badge, badgeTone = "slate", badgeTitle, icon: Icon, onClick, navigates, testId, valueClass = "text-3xl md:text-4xl", valueColor = "text-[#2563eb]", children, footer, gradient = false }) => (
  <div
    className={`group h-full rounded-xl border ${
      gradient
        ? "bg-gradient-to-b from-[#2563eb]/[0.06] to-white dark:from-[#2563eb]/[0.12] dark:to-slate-900/70"
        : "bg-white dark:bg-slate-900/70"
    } dark:border-slate-800 p-4 shadow-[0_2px_8px_rgba(15,23,42,0.08)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(37,99,235,0.14)] flex flex-col`}
    data-testid={testId ? `${testId}-card` : undefined}
  >
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      aria-label={`${label}: ${value}`}
      className="text-left disabled:cursor-default"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground/70 shrink-0" />}
      </div>
      <p
        className={`${valueClass} ${valueColor} font-bold tracking-tight mt-1 tabular-nums break-words ${
          onClick ? "group-hover:underline decoration-2 underline-offset-4" : ""
        }`}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      {hint && <p className="text-[11px] text-muted-foreground/80 mt-0.5">{hint}</p>}
      {badge && (
        <Badge
          title={badgeTitle}
          className={`mt-2 text-[10px] font-medium hover:bg-inherit ${TONE[badgeTone]} ${
            badgeTitle ? "cursor-help" : ""
          }`}
        >
          {badgeTone === "amber" && <AlertTriangle className="w-3 h-3 mr-1" />}
          {badge}
        </Badge>
      )}
    </button>
    {children && <div className="mt-3">{children}</div>}
    <div className="mt-auto pt-2 flex items-center justify-between gap-2">
      {footer}
      {onClick && (
        <span className="ml-auto hidden group-hover:inline-flex items-center gap-1 text-[11px] text-[#2563eb]">
          {navigates ? "Open in pipeline" : "View detail"}
          {navigates ? <ArrowUpRight className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
        </span>
      )}
    </div>
  </div>
);

const PaceTile = ({ label, sub, metric, month, onClick, testId, color }) => (
  <Tile
    label={label}
    value={metric.current}
    sub={sub}
    icon={Activity}
    gradient
    onClick={onClick}
    testId={testId}
    badge={CONFIDENCE[month.confidence].label}
    badgeTone={CONFIDENCE[month.confidence].tone}
    badgeTitle={CONFIDENCE[month.confidence].tip}
  >
    <Sparkline data={metric.series} color={color} />
    <dl className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
      <div className="flex justify-between gap-2">
        <dt>Daily pace</dt>
        <dd className="font-medium text-foreground tabular-nums">
          {metric.daily_pace}/day over {month.working_days_used} working day
          {month.working_days_used === 1 ? "" : "s"}
        </dd>
      </div>
      <div className="flex justify-between gap-2">
        <dt>Projected remaining</dt>
        <dd className="font-medium text-foreground tabular-nums">
          +{metric.projected_remaining} over remaining {month.working_days_remaining} day
          {month.working_days_remaining === 1 ? "" : "s"}
        </dd>
      </div>
      <div className="flex justify-between gap-2">
        <dt>Predicted month end</dt>
        <dd className="font-semibold text-foreground tabular-nums">= {metric.predicted_month_end} total</dd>
      </div>
    </dl>
    <p className="mt-1.5">
      <Change
        value={metric.current - metric.previous}
        percent={metric.previous ? Math.round(((metric.current - metric.previous) / metric.previous) * 100) : null}
      />
    </p>
  </Tile>
);

/** One stacked bar for a whole pipeline — every segment and legend row jumps to the board. */
const StageBar = ({ title, sub, stages, colors, total, onPick, testId }) => {
  const sum = stages.reduce((acc, s) => acc + s.count, 0);
  return (
    <div
      className="group h-full rounded-xl border bg-white dark:bg-slate-900/70 dark:border-slate-800 p-4 shadow-[0_2px_8px_rgba(15,23,42,0.08)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(37,99,235,0.14)]"
      data-testid={`${testId}-card`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <GitBranch className="w-4 h-4 text-muted-foreground/70 shrink-0" />
      </div>
      <p className="text-3xl md:text-4xl font-bold tracking-tight text-[#2563eb] mt-1 tabular-nums">{total}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}

      <div className="flex h-3 rounded-full overflow-hidden bg-muted mt-3" data-testid={`${testId}-bar`}>
        {sum === 0 ? (
          <div className="w-full" />
        ) : (
          stages
            .filter((stage) => stage.count > 0)
            .map((stage) => (
              <button
                key={stage.stage}
                type="button"
                title={`${stage.stage}: ${stage.count}`}
                onClick={() => onPick(stage.stage)}
                style={{ width: `${(stage.count / sum) * 100}%`, background: colors[stage.stage] || C.grey }}
                className="h-full transition-opacity hover:opacity-75"
                aria-label={`${stage.stage}: ${stage.count} referrals`}
                data-testid={`${testId}-segment-${stage.stage.replace(/\s+/g, "-").toLowerCase()}`}
              />
            ))
        )}
      </div>

      <ul className="mt-3 divide-y dark:divide-slate-800">
        {stages.map((stage) => (
          <li key={stage.stage}>
            <button
              type="button"
              onClick={() => onPick(stage.stage)}
              className="w-full flex items-center justify-between gap-2 py-1.5 text-sm hover:text-[#2563eb] transition-colors"
              aria-label={`${stage.stage}: ${stage.count} referrals`}
              data-testid={`stage-row-${stage.stage.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colors[stage.stage] || C.grey }} />
                <span className="truncate">{stage.stage}</span>
              </span>
              <span className="font-semibold tabular-nums">{stage.count}</span>
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-muted-foreground">Click a stage to work it on the pipeline board</p>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/manager`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      console.error("Dashboard load failed", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = setInterval(load, REFRESH_MS);
    const clock = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      clearInterval(refresh);
      clearInterval(clock);
    };
  }, [load]);

  const month = data?.month;
  const banner = useMemo(() => {
    if (!month) return null;
    if (month.confidence === "low")
      return `Early-stage forecast: projections rest on only ${month.working_days_used} working ${
        month.working_days_used === 1 ? "day" : "days"
      } of data. Expect them to settle after 15+ working days.`;
    if (month.confidence === "medium") return "Forecast developing — check back in a few days for firmer projections.";
    return null;
  }, [month]);

  if (loading) {
    return (
      <div className="p-6 space-y-6 max-w-[1400px] mx-auto" data-testid="dashboard-skeleton">
        <div className="h-8 w-48 rounded-lg bg-muted animate-pulse" />
        <div className="h-24 rounded-xl bg-muted animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((key) => (
            <div key={key} className="h-56 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin text-[#2563eb]" /> Loading live figures…
        </div>
      </div>
    );
  }
  if (!data) {
    return <p className="p-6 text-sm text-muted-foreground">Dashboard data could not be loaded.</p>;
  }

  const { pace, doctors, reps, appointments, referrals } = data;
  const monthKey = month.first_day.slice(0, 7);
  const toBoard = (pipeline, stage, extra = "") =>
    navigate(`/referrals?type=${pipeline}&stage=${encodeURIComponent(stage)}${extra}`);

  const visitRepData = Object.entries(reps.visits_by_rep)
    .map(([rep, value]) => ({ label: rep, short: firstName(rep), value }))
    .sort((a, b) => b.value - a.value);
  const apptRepData = Object.entries(reps.appointments_by_rep)
    .map(([rep, value]) => ({ label: rep, short: firstName(rep), value }))
    .sort((a, b) => b.value - a.value);
  const ratio = reps.appointment_ratio;
  const noAppointment = Math.max(ratio.visits - ratio.appointments, 0);
  const confirmationOver =
    appointments.avg_days_to_confirmation !== null &&
    appointments.avg_days_to_confirmation > appointments.confirmation_target_days;
  const thin = referrals.active_total < 20;

  return (
    <div className="p-3 sm:p-4 lg:p-6 pb-12 max-w-[1400px] mx-auto space-y-6" data-testid="manager-dashboard">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1" data-testid="dashboard-updated">
            {referrals.basis} · last updated {relativeTime(data.generated_at)}
          </p>
        </div>
        <OnlineNow />
      </header>

      {/* ---------- Section 1: pace & forecast ---------- */}
      <section aria-label="Pace and forecast">
        <div
          className="relative overflow-hidden rounded-xl border bg-gradient-to-r from-[#2563eb]/[0.07] via-white to-white dark:from-[#2563eb]/[0.14] dark:via-slate-900/60 dark:to-slate-900/60 dark:border-slate-800 shadow-[0_2px_8px_rgba(15,23,42,0.08)] p-5 pl-6"
          data-testid="pace-banner"
        >
          <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#2563eb]" />

          <div className="flex items-center gap-2 mb-8">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#2563eb]/10 text-[#2563eb]">
              <Activity className="w-4 h-4" />
            </span>
            <h2 className="text-base md:text-lg font-semibold tracking-tight">Pace &amp; forecast</h2>
          </div>

          <div className="mb-6">
            <p className="text-sm md:text-base font-medium" data-testid="pace-subheading">
              {new Date(month.today).toLocaleDateString("en-ZA", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
              <span className="text-muted-foreground font-normal">
                {" · "}
                {month.working_days_used} of {month.working_days_total} working days used ·{" "}
                {month.working_days_remaining} left in {month.label}
              </span>
            </p>
            {month.holidays_this_month.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Public holidays excluded: {month.holidays_this_month.map((h) => h.name).join(", ")}
              </p>
            )}
          </div>

          {banner && (
            <p
              className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-100/80 dark:bg-amber-900/25 dark:border-amber-800 px-3 py-2.5 text-xs md:text-sm text-amber-900 dark:text-amber-200"
              data-testid="forecast-banner"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{banner}</span>
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <PaceTile
            label="Visits logged"
            sub="Visits to practices this month"
            metric={pace.visits}
            month={month}
            color={C.primary}
            onClick={() => setDrill({ view: "visits_by_day" })}
            testId="pace-visits"
          />
          <PaceTile
            label="Appointments scheduled"
            sub="Appointments with doctors"
            metric={pace.appointments}
            month={month}
            color={C.cyan}
            onClick={() => setDrill({ view: "appointments_by_day" })}
            testId="pace-appointments"
          />
          <PaceTile
            label="Referrals logged"
            sub="New referrals captured"
            metric={pace.referrals}
            month={month}
            color={C.green}
            onClick={() => setDrill({ view: "referrals_by_day" })}
            testId="pace-referrals"
          />
        </div>
      </section>

      {/* ---------- Section 2: doctors ---------- */}
      <Section title="Doctors" icon={Stethoscope}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Tile
            label="Active doctors"
            value={doctors.active}
            sub={`Visited or referring in 90 days, of ${doctors.total} on the books`}
            icon={Users}
            onClick={() => setDrill({ view: "doctors" })}
            testId="doctors-active"
            footer={<Change value={doctors.active_change} />}
          >
            <StatusDonut
              slices={doctors.status_breakdown}
              total={doctors.total}
              onPick={(status) => setDrill({ view: "doctors", filter: status })}
            />
          </Tile>

          <Tile
            label="Territory coverage"
            value={`${doctors.coverage_percent}%`}
            sub="Active doctors as a share of the whole territory"
            icon={MapPin}
            onClick={() => navigate("/rep/territories")}
            testId="doctors-coverage"
            footer={<Change value={doctors.coverage_change} suffix="%" />}
          >
            <Gauge
              percent={doctors.coverage_percent}
              target={doctors.coverage_target}
              color={doctors.coverage_percent >= doctors.coverage_target ? C.green : C.primary}
            />
          </Tile>

          <Tile
            label="Top doctor this month"
            value={doctors.top_doctor ? doctors.top_doctor.name : "—"}
            valueClass="text-xl md:text-2xl"
            valueColor="text-foreground"
            sub={
              doctors.top_doctor
                ? `${doctors.top_doctor.referrals} referral(s) — highest volume this month`
                : "No referrals logged yet this month"
            }
            icon={Award}
            onClick={
              doctors.top_doctor?.doctor_id
                ? () => navigate(`/doctor-timeline?doctor=${encodeURIComponent(doctors.top_doctor.doctor_id)}`)
                : () => setDrill({ view: "doctors" })
            }
            testId="doctors-top"
          >
            {doctors.top_doctors.length > 0 ? (
              <RankBars
                items={doctors.top_doctors.map((doctor) => ({
                  label: doctor.name,
                  value: doctor.referrals,
                  testId: `top-doctor-${doctor.name.replace(/\s+/g, "-").toLowerCase()}`,
                  doctor,
                }))}
                onPick={(item) =>
                  item.doctor.doctor_id
                    ? navigate(`/doctor-timeline?doctor=${encodeURIComponent(item.doctor.doctor_id)}`)
                    : setDrill({ view: "doctors" })
                }
                testId="top-doctors-bars"
              />
            ) : (
              <p className="text-[11px] text-muted-foreground">Referral volume by doctor appears here once logged.</p>
            )}
          </Tile>

          <Tile
            label="At-risk doctors"
            value={doctors.at_risk_count}
            valueColor={doctors.at_risk_count > 0 ? "text-red-600" : "text-[#2563eb]"}
            sub="Visited, but no referral in 60+ days"
            badge={doctors.at_risk_count > 0 ? "Needs re-engagement" : "All clear"}
            badgeTone={doctors.at_risk_count > 0 ? "amber" : "emerald"}
            icon={AlertTriangle}
            onClick={() => setDrill({ view: "at_risk" })}
            testId="doctors-at-risk"
          >
            <ul className="space-y-1.5" data-testid="at-risk-list">
              {doctors.at_risk_list.length === 0 ? (
                <li className="text-[11px] text-muted-foreground">Every visited doctor has referred recently.</li>
              ) : (
                doctors.at_risk_list.map((doctor) => (
                  <li key={doctor.doctor_id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/doctor-timeline?doctor=${encodeURIComponent(doctor.doctor_id)}`)}
                      className="w-full flex items-center gap-2 text-[11px] text-left hover:text-[#2563eb] transition-colors"
                    >
                      <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                      <span className="truncate">{doctor.name}</span>
                      <span className="ml-auto text-muted-foreground whitespace-nowrap">
                        last visit {doctor.days_since_visit}d ago
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </Tile>
        </div>
      </Section>

      {/* ---------- Section 3: reps ---------- */}
      <Section title="Reps — visits & appointments" icon={ClipboardList}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Tile
            label="Total visits logged"
            value={reps.visits_this_month}
            sub="Rep presence in territory — practice walk-ins and initial contacts"
            hint={`${reps.avg_per_rep} per rep across ${reps.rep_count} reps`}
            icon={ClipboardList}
            onClick={() => setDrill({ view: "rep_visits" })}
            testId="reps-visits"
            footer={<Change value={reps.visits_this_month - data.pace.visits.previous} />}
          >
            {visitRepData.length > 0 ? (
              <RepBars data={visitRepData} onPick={() => setDrill({ view: "rep_visits" })} />
            ) : (
              <p className="text-[11px] text-muted-foreground">No visits logged this month yet.</p>
            )}
          </Tile>

          <Tile
            label="Appointments with doctors"
            value={reps.appointments_this_month}
            sub="Scheduled doctor meetings — actual face-to-face engagement"
            hint={`${reps.avg_appointments_per_rep} per rep across ${reps.rep_count} reps`}
            icon={CalendarCheck}
            onClick={() => setDrill({ view: "rep_appointments" })}
            testId="reps-appointments"
            footer={<Change value={reps.appointments_this_month - data.pace.appointments.previous} />}
          >
            {apptRepData.length > 0 ? (
              <RepBars
                data={apptRepData}
                onPick={() => setDrill({ view: "rep_appointments" })}
                palette={[C.cyan, C.primary, C.green, C.purple, C.amber]}
              />
            ) : (
              <p className="text-[11px] text-muted-foreground">No appointments booked this month yet.</p>
            )}
          </Tile>

          <Tile
            label="Top performer this month"
            value={reps.top_performer ? reps.top_performer.rep : "—"}
            valueClass="text-xl md:text-2xl"
            sub={
              reps.top_performer
                ? `${reps.top_performer.visits} visits · ${reps.top_performer.appointments} appointments`
                : "No rep activity logged yet this month"
            }
            hint={
              reps.top_performer
                ? `${reps.top_performer.commission_eligible} case(s) commission eligible`
                : undefined
            }
            icon={Award}
            onClick={
              reps.top_performer
                ? () => navigate(`/manager/rep-territory/${encodeURIComponent(reps.top_performer.rep)}`)
                : undefined
            }
            testId="reps-top"
          >
            {reps.top_performer && (
              <RankBars
                items={[
                  { label: "Visits", value: reps.top_performer.visits, color: C.primary },
                  { label: "Appointments", value: reps.top_performer.appointments, color: C.green },
                ]}
                testId="top-performer-bars"
              />
            )}
          </Tile>

          <Tile
            label="Appointments per visit logged"
            value={ratio.percent === null ? "—" : `${ratio.percent}%`}
            sub={`${ratio.appointments} appointments against ${ratio.visits} visits logged`}
            hint={reps.ratio_basis}
            icon={GaugeIcon}
            onClick={() => setDrill({ view: "rep_efficiency" })}
            testId="reps-efficiency"
            footer={<Change value={ratio.change} suffix="%" />}
          >
            <SegmentBar
              segments={[
                { label: "Appointments booked", value: ratio.appointments, color: C.green, testId: "ratio-appointments" },
                { label: "Visits with no appointment", value: noAppointment, color: C.grey, testId: "ratio-no-appointment" },
              ]}
              onPick={() => setDrill({ view: "rep_efficiency" })}
              testId="reps-ratio-bar"
            />
            <ul className="mt-2 space-y-0.5 text-[11px]">
              {reps.by_rep
                .filter((row) => row.visits || row.appointments)
                .map((row) => (
                  <li key={row.rep} className="flex justify-between gap-2">
                    <span className="truncate">{row.rep}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {row.appointments}/{row.visits}
                      {row.percent === null ? "" : ` (${row.percent}%)`}
                    </span>
                  </li>
                ))}
            </ul>
          </Tile>
        </div>
      </Section>

      {/* ---------- Section 4: pipeline confirmations ---------- */}
      <Section title="Pipeline confirmations" icon={CalendarCheck}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Tile
            label="Appointment confirmation rate"
            value={appointments.confirm_rate === null ? "—" : `${appointments.confirm_rate}%`}
            sub={
              appointments.decided
                ? `${appointments.confirmed} of ${appointments.decided} answered this month`
                : "No confirmations answered yet this month"
            }
            hint="Sleep study and titration appointments confirmed by patients"
            icon={CheckCircle2}
            onClick={() => setDrill({ view: "appointment_confirmation" })}
            testId="appointments-confirm-rate"
            footer={<Change value={appointments.confirm_rate_change} suffix="%" />}
          >
            <SegmentBar
              segments={[
                { label: "Confirmed", value: appointments.confirmed, color: C.green, testId: "confirm-yes" },
                { label: "Declined or cancelled", value: appointments.declined, color: C.red, testId: "confirm-no" },
              ]}
              onPick={(segment) =>
                setDrill({
                  view: "appointment_confirmation",
                  filter: segment.label === "Confirmed" ? "Yes" : null,
                })
              }
              testId="confirm-bar"
            />
          </Tile>

          <Tile
            label="Avg days to confirmation"
            value={num(appointments.avg_days_to_confirmation)}
            sub="Referral to the patient confirming their appointment"
            hint={`Target under ${appointments.confirmation_target_days} days`}
            badge={
              appointments.avg_days_to_confirmation === null
                ? "Nothing answered yet"
                : confirmationOver
                ? "Over target"
                : "On target"
            }
            badgeTone={
              appointments.avg_days_to_confirmation === null ? "slate" : confirmationOver ? "amber" : "emerald"
            }
            icon={Clock}
            onClick={() => setDrill({ view: "confirmation_speed" })}
            testId="appointments-confirmation-speed"
            footer={<Change value={appointments.avg_days_to_confirmation_change} suffix=" days" />}
          >
            <Sparkline data={appointments.confirmation_speed_series} color={C.purple} suffix=" days" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Referral to study scheduled sits at {num(appointments.avg_days_to_appointment)} day(s)
            </p>
          </Tile>

          <Tile
            label="Unconfirmed appointments"
            value={appointments.unconfirmed_count}
            valueColor={appointments.unconfirmed_count > 0 ? "text-amber-600" : "text-[#2563eb]"}
            sub="Awaiting patient confirmation on a sleep study or titration"
            badge={appointments.unconfirmed_count > 0 ? "Watch" : "Clear"}
            badgeTone={appointments.unconfirmed_count > 0 ? "amber" : "emerald"}
            icon={Hourglass}
            onClick={() => setDrill({ view: "unconfirmed" })}
            testId="appointments-unconfirmed"
            footer={
              <button
                type="button"
                onClick={() => setDrill({ view: "blocked" })}
                className="text-[11px] text-muted-foreground hover:text-[#2563eb] transition-colors"
                data-testid="appointments-blocked"
              >
                {appointments.blocked} blocked by a delay or cancellation
              </button>
            }
          >
            <ul className="space-y-1.5" data-testid="unconfirmed-list">
              {appointments.unconfirmed_list.length === 0 ? (
                <li className="text-[11px] text-muted-foreground">Every appointment has an answer.</li>
              ) : (
                appointments.unconfirmed_list.map((item) => (
                  <li key={item.tin_number}>
                    <div className="flex items-baseline justify-between gap-2 text-[11px]">
                      <span className="truncate">
                        {item.patient} <span className="text-muted-foreground">· {item.which}</span>
                      </span>
                      <span className="tabular-nums text-muted-foreground whitespace-nowrap">
                        {item.days_waiting}d waiting
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-0.5">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min((item.days_waiting / 14) * 100, 100)}%`,
                          background: item.days_waiting > 5 ? C.red : item.days_waiting > 2 ? C.amber : C.green,
                        }}
                      />
                    </div>
                  </li>
                ))
              )}
            </ul>
          </Tile>
        </div>
      </Section>

      {/* ---------- Section 5: referrals ---------- */}
      <Section title="Referrals" icon={GitBranch}>
        {thin && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-200"
            data-testid="referrals-banner"
          >
            Early-stage data: only {referrals.active_total} referrals are active. These numbers will steady as the
            caseload grows.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Tile
            label="Active referrals in pipeline"
            value={referrals.active_total}
            sub="Open across the sleep study and titration pipelines"
            hint="Trend below is referrals logged per day, last 30 days"
            icon={GitBranch}
            onClick={() => setDrill({ view: "pipeline_trend" })}
            testId="referrals-active"
          >
            <AreaTrend data={referrals.trend_30d} />
          </Tile>

          <StageBar
            title="Sleep study pipeline — all stages"
            sub="Where referrals sit in the sleep study journey"
            stages={referrals.ss_stages}
            colors={SS_STAGE_COLOR}
            total={referrals.ss_stages.reduce((sum, stage) => sum + stage.count, 0)}
            onPick={(stage) => toBoard("ss", stage)}
            testId="ss-stages"
          />

          <StageBar
            title="In titration pipeline"
            sub="SS Feedback through TT Feedback"
            stages={referrals.tt_stages}
            colors={TT_STAGE_COLOR}
            total={referrals.tt_active}
            onPick={(stage) => toBoard("tt", stage)}
            testId="tt-stages"
          />

          <Tile
            label="Sleep studies completed this month"
            value={referrals.ss_done_month}
            sub="Patients diagnosed, study reported, ready for titration"
            icon={CheckCircle2}
            onClick={() => toBoard("ss", "Done", `&done_month=${monthKey}`)}
            navigates
            testId="referrals-ss-done"
            footer={<Change value={referrals.ss_done_change} />}
          >
            <Ring value={referrals.ss_done_month} benchmark={referrals.ss_done_last_month} />
          </Tile>

          <Tile
            label="Titrations completed this month"
            value={referrals.tt_done_month}
            sub="Patients fitted with a machine and followed up"
            hint="Machine fitted uses the TT Facilitation date — no dedicated fitted date exists yet"
            icon={CheckCircle2}
            onClick={() => toBoard("tt", "Done", `&done_month=${monthKey}`)}
            navigates
            testId="referrals-tt-done"
            footer={<Change value={referrals.tt_done_change} />}
          >
            <Ring value={referrals.tt_done_month} benchmark={referrals.tt_done_last_month} color={C.cyan} />
          </Tile>

          <Tile
            label="Sleep study to titration conversion"
            value={referrals.ss_to_tt_percent === null ? "—" : `${referrals.ss_to_tt_percent}%`}
            sub="Diagnosed patients reaching machine placement"
            badge={
              referrals.ss_to_tt_percent === null
                ? "No data yet"
                : referrals.ss_to_tt_percent >= referrals.ss_to_tt_target
                ? "On target"
                : "Below target"
            }
            badgeTone={
              referrals.ss_to_tt_percent === null
                ? "slate"
                : referrals.ss_to_tt_percent >= referrals.ss_to_tt_target
                ? "emerald"
                : "amber"
            }
            icon={TrendingUp}
            onClick={() => setDrill({ view: "conversion" })}
            testId="referrals-conversion"
          >
            <Funnel
              top={referrals.ss_done_month}
              bottom={referrals.tt_done_month}
              dropoff={referrals.ss_to_tt_dropoff}
              percent={referrals.ss_to_tt_percent}
            />
          </Tile>
        </div>
      </Section>

      <p className="text-xs text-muted-foreground">
        Live from MongoDB · refreshes every 5 minutes · updated {relativeTime(data.generated_at)}
      </p>

      <DrilldownModal
        open={Boolean(drill)}
        onClose={() => setDrill(null)}
        view={drill?.view}
        stage={drill?.stage}
        pipeline={drill?.pipeline}
        initialFilter={drill?.filter}
      />
    </div>
  );
};

export default Dashboard;
